import {
	assertProjectCanvasDocument,
	type ProjectCanvasDocument,
	type ProjectCanvasEdge,
	type ProjectCanvasGenerationRecord,
	type ProjectCanvasNode,
} from "./ProjectCanvasDocumentBoundary";

export interface PublicProjectOwner {
	readonly id: string | null;
	readonly displayName: string;
	readonly avatarUrl: string | null;
	readonly bio: string | null;
}

export interface PublicProjectShare {
	readonly document: ProjectCanvasDocument;
	readonly owner: PublicProjectOwner;
}

export class PublicProjectShareUnavailableError extends Error {
	constructor(message: string, options?: ErrorOptions) {
		super(message, options);
		this.name = "PublicProjectShareUnavailableError";
	}
}

export class PublicProjectShareNotFoundError extends Error {
	constructor() {
		super("The shared project link is invalid or has been revoked");
		this.name = "PublicProjectShareNotFoundError";
	}
}

export async function fetchPublicProjectShare(
	slug: string,
	environment: Readonly<{
		VITE_SUPABASE_URL: string | undefined;
		VITE_SUPABASE_PUBLISHABLE_KEY: string | undefined;
	}> = {
		VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
		VITE_SUPABASE_PUBLISHABLE_KEY:
			import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
	},
): Promise<PublicProjectShare> {
	const normalizedSlug = slug.trim();
	if (!/^[A-Za-z0-9_-]{16,128}$/u.test(normalizedSlug)) {
		throw new PublicProjectShareNotFoundError();
	}

	const origin = requiredEnvironmentValue(
		environment.VITE_SUPABASE_URL,
		"VITE_SUPABASE_URL",
	).replace(/\/$/u, "");
	const publishableKey = requiredEnvironmentValue(
		environment.VITE_SUPABASE_PUBLISHABLE_KEY,
		"VITE_SUPABASE_PUBLISHABLE_KEY",
	);

	const response = await fetch(`${origin}/rest/v1/rpc/resolve_project_public_link`, {
		method: "POST",
		headers: {
			apikey: publishableKey,
			authorization: `Bearer ${publishableKey}`,
			"content-type": "application/json",
		},
		body: JSON.stringify({ p_slug: normalizedSlug }),
		cache: "no-store",
	});

	if (!response.ok) {
		throw new PublicProjectShareUnavailableError(
			`Project share request failed with status ${response.status}`,
		);
	}

	const payload: unknown = await response.json();
	if (!isRecord(payload)) {
		throw new PublicProjectShareUnavailableError(
			"Project share response is not an object",
		);
	}
	if (payload.ok !== true) {
		if (payload.error_code === "SHARE_NOT_FOUND") {
			throw new PublicProjectShareNotFoundError();
		}
		throw new PublicProjectShareUnavailableError(
			"Project share response was rejected",
		);
	}

	const document = normalizeDocument(payload, origin);
	assertProjectCanvasDocument(document);
	return {
		document,
		owner: normalizeOwner(payload.owner),
	};
}

function normalizeDocument(
	payload: Readonly<Record<string, unknown>>,
	supabaseOrigin: string,
): ProjectCanvasDocument {
	const project = record(payload.project, "project");
	const projectId = uuid(project.id, "project.id");
	const nodes = array(payload.nodes, "nodes").map((value) =>
		normalizeNode(value, projectId, supabaseOrigin),
	);
	const edges = array(payload.edges, "edges").map((value) =>
		normalizeEdge(value, projectId),
	);
	const generationRecords = array(
		payload.generation_records ?? [],
		"generation_records",
	).map((value) => normalizeGenerationRecord(value, projectId, edges));
	const updatedAt = epoch(project.updated_at, "project.updated_at");

	return {
		project: {
			id: projectId,
			kind: "project",
			title: { kind: "literal", value: nonEmpty(project.title, "project.title") },
			ownerUserId: null,
			releaseAlbumId: optionalUuid(project.release_album_id),
			releaseTrackId: optionalUuid(project.release_track_id),
			releaseVideoId: optionalUuid(project.release_video_id),
			createdAtEpochMilliseconds: epoch(project.created_at, "project.created_at"),
			updatedAtEpochMilliseconds: updatedAt,
			deletedAtEpochMilliseconds: optionalEpoch(project.deleted_at),
		},
		nodes,
		edges,
		generationRecords,
		version: 0,
		updatedAtEpochMilliseconds: updatedAt,
	};
}

function normalizeNode(
	value: unknown,
	projectId: string,
	supabaseOrigin: string,
): ProjectCanvasNode {
	const node = record(value, "node");
	const mediaAsset = isRecord(node.media_asset) ? node.media_asset : null;
	const mediaUrl = publicMediaUrl(mediaAsset, supabaseOrigin);
	const artworkUrl = optionalUrl(node.track_artwork_url)
		?? optionalUrl(node.album_artwork_url);
	const createdAt = epoch(node.created_at, "node.created_at");
	const updatedAt = optionalEpoch(node.updated_at) ?? createdAt;
	const assetKind = nonEmpty(node.asset_kind, "node.asset_kind");
	if (![
		"text",
		"lyrics",
		"music",
		"tts",
		"image",
		"video",
		"asset_3d",
	].includes(assetKind)) {
		throw new PublicProjectShareUnavailableError("Unsupported project node kind");
	}

	return {
		id: uuid(node.id, "node.id"),
		projectId,
		assetKind: assetKind as ProjectCanvasNode["assetKind"],
		title: { kind: "literal", value: nonEmpty(node.title, "node.title") },
		subtitle: optionalString(node.subtitle),
		position: {
			x: finiteNumber(node.position_x, "node.position_x"),
			y: finiteNumber(node.position_y, "node.position_y"),
		},
		size: {
			width: positiveNumber(node.width, "node.width"),
			height: positiveNumber(node.height, "node.height"),
		},
		zIndex: integer(node.z_index, "node.z_index"),
		mediaUrl,
		thumbnailUrl: artworkUrl ?? mediaUrl,
		widthPixels: optionalInteger(node.parameter_width ?? mediaAsset?.width),
		heightPixels: optionalInteger(node.parameter_height ?? mediaAsset?.height),
		durationMilliseconds: optionalDurationMilliseconds(
			node.parameter_duration_seconds,
		),
		format: optionalString(node.parameter_format),
		textContent: optionalString(node.text_excerpt),
		createdAtEpochMilliseconds: createdAt,
		updatedAtEpochMilliseconds: updatedAt,
		deletedAtEpochMilliseconds: optionalEpoch(node.deleted_at),
	};
}

function normalizeEdge(value: unknown, projectId: string): ProjectCanvasEdge {
	const edge = record(value, "edge");
	const relationship = nonEmpty(edge.relationship, "edge.relationship");
	if (!["input", "reference", "generated"].includes(relationship)) {
		throw new PublicProjectShareUnavailableError("Unsupported project edge relationship");
	}
	const createdAt = epoch(edge.created_at, "edge.created_at");
	return {
		id: uuid(edge.id, "edge.id"),
		projectId,
		sourceNodeId: uuid(edge.source_node_id, "edge.source_node_id"),
		targetNodeId: uuid(edge.target_node_id, "edge.target_node_id"),
		relationship: relationship as ProjectCanvasEdge["relationship"],
		createdAtEpochMilliseconds: createdAt,
		updatedAtEpochMilliseconds: createdAt,
		deletedAtEpochMilliseconds: optionalEpoch(edge.deleted_at),
	};
}

function normalizeGenerationRecord(
	value: unknown,
	projectId: string,
	edges: readonly ProjectCanvasEdge[],
): ProjectCanvasGenerationRecord {
	const generation = record(value, "generation record");
	const outputNodeId = uuid(generation.output_node_id, "generation.output_node_id");
	const incoming = edges.filter(
		(edge) => edge.targetNodeId === outputNodeId && edge.deletedAtEpochMilliseconds === null,
	);
	const focusedNodeId = incoming.find((edge) => edge.relationship === "input")
		?.sourceNodeId ?? null;
	const referenceNodeIds = incoming
		.filter((edge) => edge.relationship === "reference")
		.map((edge) => edge.sourceNodeId);

	return {
		id: uuid(generation.id, "generation.id"),
		projectId,
		outputNodeId,
		focusedNodeId,
		referenceNodeIds,
		lineageKey: `${projectId}:${outputNodeId}`,
		sourceJobTable: optionalString(generation.source_job_table),
		sourceJobId: optionalUuid(generation.source_job_id),
		provider: optionalString(generation.provider),
		modelKey: optionalString(generation.model_key),
		modelDisplayName: optionalString(generation.model_display_name),
		prompt: optionalString(generation.prompt),
		negativePrompt: optionalString(generation.negative_prompt),
		mode: optionalString(generation.mode),
		configurationSummary: optionalString(generation.configuration_summary),
		creditsUsed: optionalInteger(generation.credits_used),
		idempotencyKey: optionalString(generation.idempotency_key),
		createdAtEpochMilliseconds: epoch(
			generation.created_at,
			"generation.created_at",
		),
	};
}

function normalizeOwner(value: unknown): PublicProjectOwner {
	const owner = isRecord(value) ? value : {};
	return {
		id: optionalUuid(owner.id),
		displayName: optionalString(owner.display_name) ?? "Artist",
		avatarUrl: optionalUrl(owner.avatar_url),
		bio: optionalString(owner.bio),
	};
}

function publicMediaUrl(
	mediaAsset: Readonly<Record<string, unknown>> | null,
	supabaseOrigin: string,
): string | null {
	if (mediaAsset === null || mediaAsset.visibility !== "public") return null;
	const bucket = optionalString(mediaAsset.bucket_id);
	const objectPath = optionalString(mediaAsset.object_path);
	if (bucket === null || objectPath === null) return null;
	return `${supabaseOrigin}/storage/v1/object/public/${encodeURIComponent(bucket)}/${objectPath
		.split("/")
		.map(encodeURIComponent)
		.join("/")}`;
}

function requiredEnvironmentValue(value: string | undefined, name: string): string {
	const normalized = value?.trim();
	if (!normalized) {
		throw new PublicProjectShareUnavailableError(`${name} is not configured`);
	}
	return normalized;
}

function record(value: unknown, label: string): Readonly<Record<string, unknown>> {
	if (!isRecord(value)) {
		throw new PublicProjectShareUnavailableError(`${label} is not an object`);
	}
	return value;
}

function array(value: unknown, label: string): readonly unknown[] {
	if (!Array.isArray(value)) {
		throw new PublicProjectShareUnavailableError(`${label} is not an array`);
	}
	return value;
}

function nonEmpty(value: unknown, label: string): string {
	if (typeof value !== "string" || value.trim() === "") {
		throw new PublicProjectShareUnavailableError(`${label} is missing`);
	}
	return value.trim();
}

function uuid(value: unknown, label: string): string {
	const normalized = nonEmpty(value, label).toLowerCase();
	if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u.test(normalized)) {
		throw new PublicProjectShareUnavailableError(`${label} is not a UUID`);
	}
	return normalized;
}

function optionalUuid(value: unknown): string | null {
	if (value === null || value === undefined || value === "") return null;
	return uuid(value, "optional UUID");
}

function optionalString(value: unknown): string | null {
	if (typeof value !== "string") return null;
	const normalized = value.trim();
	return normalized === "" ? null : normalized;
}

function optionalUrl(value: unknown): string | null {
	const normalized = optionalString(value);
	if (normalized === null) return null;
	try {
		const url = new URL(normalized);
		return url.protocol === "https:" ? url.href : null;
	} catch {
		return null;
	}
}

function finiteNumber(value: unknown, label: string): number {
	if (typeof value !== "number" || !Number.isFinite(value)) {
		throw new PublicProjectShareUnavailableError(`${label} is not finite`);
	}
	return value;
}

function positiveNumber(value: unknown, label: string): number {
	const number = finiteNumber(value, label);
	if (number <= 0) {
		throw new PublicProjectShareUnavailableError(`${label} is not positive`);
	}
	return number;
}

function integer(value: unknown, label: string): number {
	const number = finiteNumber(value, label);
	if (!Number.isInteger(number)) {
		throw new PublicProjectShareUnavailableError(`${label} is not an integer`);
	}
	return number;
}

function optionalInteger(value: unknown): number | null {
	if (value === null || value === undefined) return null;
	return integer(value, "optional integer");
}

function optionalDurationMilliseconds(value: unknown): number | null {
	const seconds = optionalInteger(value);
	return seconds === null ? null : seconds * 1_000;
}

function epoch(value: unknown, label: string): number {
	if (typeof value !== "string") {
		throw new PublicProjectShareUnavailableError(`${label} is not a timestamp`);
	}
	const parsed = Date.parse(value);
	if (!Number.isFinite(parsed) || parsed < 0) {
		throw new PublicProjectShareUnavailableError(`${label} is invalid`);
	}
	return parsed;
}

function optionalEpoch(value: unknown): number | null {
	if (value === null || value === undefined) return null;
	return epoch(value, "optional timestamp");
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}
